import type { CommandEntry } from "@/shared/components/command-menu/command-match";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { CommandItem } from "@/shared/components/ui/command";
import { Skeleton } from "@/shared/components/ui/skeleton";

export function initialsFor(label: string): string {
  return label
    .split(" ")
    .slice(0, 2)
    .map((part) => part.slice(0, 1).toUpperCase())
    .join("");
}

/// One row. People get an avatar, everything else gets its Lucide icon.
/// `value` is the entry id rather than the label because cmdk filtering is
/// disabled here and ids are what the select handler resolves against.
export function CommandEntryRow({
  entry,
  onSelect,
}: {
  entry: CommandEntry;
  onSelect: (entry: CommandEntry) => void;
}) {
  const Icon = entry.icon;
  return (
    <CommandItem value={entry.id} onSelect={() => onSelect(entry)}>
      {entry.kind === "person" ? (
        <Avatar className="size-5">
          <AvatarImage src={entry.avatarUrl ?? undefined} alt="" />
          <AvatarFallback className="text-3xs">{initialsFor(entry.label)}</AvatarFallback>
        </Avatar>
      ) : (
        <Icon className="text-text-tertiary" />
      )}
      <span className="truncate">{entry.label}</span>
      {entry.hint ? (
        <span className="ml-auto truncate pl-3 text-xs text-text-tertiary">{entry.hint}</span>
      ) : null}
    </CommandItem>
  );
}

/// People rows, or the states around them. The caller wraps this in a
/// CommandGroup so the heading stays put while results swap in.
export function PeopleGroupBody({
  people,
  isPending,
  isError,
  onSelect,
}: {
  people: CommandEntry[];
  isPending: boolean;
  isError: boolean;
  onSelect: (entry: CommandEntry) => void;
}) {
  if (isError) {
    // Quiet inline failure, not a toast: the static results above are still
    // useful and a modal-level error would bury them.
    return <div className="px-3 py-2 text-xs text-text-tertiary">Couldn't load people</div>;
  }

  if (isPending && people.length === 0) {
    return (
      <div className="flex flex-col gap-1.5 px-3 py-2">
        <Skeleton className="h-5 w-40 rounded-md" />
        <Skeleton className="h-5 w-32 rounded-md" />
        <Skeleton className="h-5 w-36 rounded-md" />
      </div>
    );
  }

  return (
    <>
      {people.map((entry) => (
        <CommandEntryRow key={entry.id} entry={entry} onSelect={onSelect} />
      ))}
    </>
  );
}
